
import type { IPublicClientApplication, AccountInfo } from "@azure/msal-browser";
import { publishApiError } from "../../events";
import type { IBatchRequest, IGraphBatchRequest, IGraphBatchResponse, IGroupDetail, IIdentityDetails, IIdentityInfo, IUserDetail, IUserInfo, IGraphUserInfo, IGraphGroupInfo, IGraphResponse } from "../../interfaces";
import { sendBlobGETRequest, sendGETRequest, sendPOSTRequest } from "../requestHelper/requestHelper";
import { acquireAccessTokenAsync } from "../../AuthHelper";
import { graphRequest, groupPickerRequest } from "../../authConfig";

const graphBaseUrl = "https://graph.microsoft.com/v1.0";
const batchSize = 20;

/**
 * MS Graph API endpoints
 */
const graphConfig = {
    graphMeEndpoint: `${graphBaseUrl}/me`,
    graphMePhotoEndpoint: `${graphBaseUrl}/me/photo/$value`,
};

const getRelativeGroupDetailsEndpoint = (groupId: string): string => {
    return `/groups/${groupId}?$select=id,displayName,mail,description`;
};

const getRelativeUserDetailsEndpoint = (userId: string): string => {
    return `/users/${userId}?$select=id,displayName,mail`;
};

export async function getUserPictureObjectUrl(accessToken: string): Promise<string> {
    const blobResponse = await sendBlobGETRequest(graphConfig.graphMePhotoEndpoint, accessToken);
    return URL.createObjectURL(blobResponse);
}

export async function getUserPictureByIdAsync(userId: string, accessToken: string): Promise<Blob | null> {
    try {
        const requestUrl = `${graphBaseUrl}/users/${userId}/photo/$value`;
        return await sendBlobGETRequest(requestUrl, accessToken);
    } catch (error) {
        console.debug(`Failed to fetch user picture for ${userId}:`, error);
        return null;
    }
}

export async function getUserInfo(accessToken: string): Promise<IUserInfo> {
    return await sendGETRequest<IUserInfo>(graphConfig.graphMeEndpoint, accessToken);
}

const userSearchFields = ["displayName", "givenName", "surname"];
const groupSearchFields = ["displayName"];

/**
 * Builds an OData filter matching searchFor as a prefix of any of the given fields.
 * displayName is conventionally "givenName surname", so a startswith on it alone would
 * return nothing for someone searching by surname even though the user does exist.
 * @param searchFor the raw text typed by the user
 * @param fields the properties to match the prefix against
 * @returns a URL-encoded $filter value
 */
function buildPrefixFilter(searchFor: string, fields: string[]): string {
    const term = searchFor.replace(/'/g, "''");
    const clauses = fields.map(f => `startswith(${f},'${term}')`).join(" or ");
    return encodeURIComponent(clauses);
}

export async function searchUsersAsync(searchFor: string, msalInstance: IPublicClientApplication, account: AccountInfo): Promise<IGraphUserInfo[]> {
    try {
        const accessToken = await acquireAccessTokenAsync(msalInstance, account, graphRequest.scopes);
        const filter = searchFor === "" ? "" : `?$filter=${buildPrefixFilter(searchFor, userSearchFields)}`;
        const requestUrl: string = `${graphBaseUrl}/users${filter}`;
        const usersResponse = await sendGETRequest<IGraphResponse<IGraphUserInfo[]>>(requestUrl, accessToken);
        const users = usersResponse?.value ?? [];
        return users.map(u => ({ ...u, type: "user" as const }));
    } catch (error) {
        console.debug(`Failed to search users:`, error);
        return [];
    }
}

export async function searchGroupsAsync(searchFor: string, msalInstance: IPublicClientApplication, account: AccountInfo): Promise<IGraphGroupInfo[]> {
    try {
        const accessToken = await acquireAccessTokenAsync(msalInstance, account, groupPickerRequest.scopes);
        const selectClause = `$select=id,displayName,mail,description`;
        const filterClause = searchFor === "" ? "" : `&$filter=${buildPrefixFilter(searchFor, groupSearchFields)}`;
        const requestUrl = `${graphBaseUrl}/groups?${selectClause}${filterClause}`;
        const groupsResponse = await sendGETRequest<IGraphResponse<IGraphGroupInfo[]>>(requestUrl, accessToken);
        const groups = groupsResponse?.value ?? [];
        return groups.map(g => ({ ...g, type: "group" as const }));
    } catch (error) {
        console.debug(`Failed to search groups:`, error);
        return [];
    }
}

async function sendGraphBatchRequest(requestObject: IGraphBatchRequest, accessToken: string, abortController: AbortController): Promise<IGraphBatchResponse> {
    const requestUrl = `${graphBaseUrl}/$batch`;
    return await sendPOSTRequest<IGraphBatchResponse, IGraphBatchRequest>(requestUrl, accessToken, requestObject, abortController);
}

export async function getDetailsForUsersAndGroupsAsync(instance: IPublicClientApplication, account: AccountInfo, userIds: string[] = [], groupIds: string[] = [], abortController: AbortController): Promise<IIdentityDetails> {
    try {
        let userDetails: IUserDetail[] = [];
        let groupDetails: IGroupDetail[] = [];

        if (userIds.length > 0) {
            userDetails = await fetchUserDetailsInBatchesAsync(userIds, instance, account, abortController);
        }

        if (groupIds.length > 0) {
            groupDetails = await fetchGroupDetailsInBatchesAsync(groupIds, instance, account, abortController);
        }
        return { userDetails, groupDetails };
    
    } catch (error) {
        console.debug(error);
        publishApiError((error as Error)?.message);
        return { userDetails: [], groupDetails: [] };
    }
}

async function fetchUserDetailsInBatchesAsync(userIds: string[], instance: IPublicClientApplication, account: AccountInfo, abortController: AbortController): Promise<IUserDetail[]> {
    const userDetails: IUserDetail[] = [];

    try {
        const userAccessToken = await acquireAccessTokenAsync(instance, account, graphRequest.scopes);

        for (let i = 0; i < userIds.length; i += batchSize) {
            const batchUserIds = userIds.slice(i, i + batchSize);
            const userBatchRequests: IBatchRequest[] = batchUserIds.map(userId => ({
                id: `user-${userId}`,
                method: "GET",
                url: getRelativeUserDetailsEndpoint(userId)
            }));

            const userBatchResponse = await sendGraphBatchRequest({ requests: userBatchRequests }, userAccessToken, abortController);
            userBatchResponse.responses.forEach(response => {
                if (response.id.startsWith('user-')) {
                    const userInfo = response.body as IIdentityInfo;
                    userDetails.push({
                        id: userInfo.id,
                        email: userInfo.mail,
                        displayName: userInfo.displayName,
                        type: "user"
                    });
                }
            });
        }
    } catch (error) {
        console.debug("Failed to fetch user details", error);
    }


    return userDetails;
}


async function fetchGroupDetailsInBatchesAsync(groupIds: string[], instance: IPublicClientApplication, account: AccountInfo, abortController: AbortController): Promise<IGroupDetail[]> {
    const groupDetails: IGroupDetail[] = [];
    try {

        const groupAccessToken = await acquireAccessTokenAsync(instance, account, groupPickerRequest.scopes);

        for (let i = 0; i < groupIds.length; i += batchSize) {
            const batchGroupIds = groupIds.slice(i, i + batchSize);
            const groupBatchRequests: IBatchRequest[] = batchGroupIds.map(groupId => ({
                id: `group-${groupId}`,
                method: "GET",
                url: getRelativeGroupDetailsEndpoint(groupId)
            }));

            const groupBatchResponse = await sendGraphBatchRequest({ requests: groupBatchRequests }, groupAccessToken, abortController);
            groupBatchResponse.responses.forEach(response => {
                if (response.id.startsWith('group-')) {
                    const groupInfo = response.body as IIdentityInfo;
                    groupDetails.push({
                        id: groupInfo.id,
                        displayName: groupInfo.displayName,
                        email: groupInfo.mail,
                        description: groupInfo.description ?? "",
                        type: "group"
                    });
                }
            });
        }

    } catch (error) {
        console.debug("Failed to fetch group details", error);
    }

    return groupDetails;
}